// Chapter 23 - Chaining
// Source: Cooper, Heron, & Heward (3rd ed.), Chapter 23.

import type { ChapterModule } from "./types";

const chapter23Module: ChapterModule = {
  meta: {
    id: "23",
    number: 23,
    title: "Chaining",
    subtitle: "Task analysis, forward/backward/total task chaining",
    part: "Part 8 - Developing New Behavior",
    estMinutes: 70,
    bacb: ["G-8"],
  },
  curated: true,
  overview: [
    "Chapter 23 covers BEHAVIOR CHAINS - linked sequences of responses leading to a terminal outcome - and CHAINING procedures used to teach them. Each link in a chain has dual function: it is a CONDITIONED REINFORCER for the previous response and an SD for the next. Reinforcement for the final response maintains the entire chain.",
    "You will learn task analysis (the foundation of chaining), assessment methods (single-opportunity, multiple-opportunity), the chaining variants (FORWARD, BACKWARD, BACKWARD WITH LEAP AHEADS, TOTAL TASK), how to choose between them, six ways behavior chains break (extinction, satiation, unchaining, interrupting the chain, substituting an SD, extending/delaying the chain), troubleshooting steps, and the factors that affect chain performance.",
  ],
  objectives: [
    { id: "G-8", text: "Use task analysis and chaining (forward, backward, total task) to teach complex behaviors." },
  ],
  coreConcepts: [
    {
      title: "What is a behavior chain",
      body: [
        "A behavior chain is a linked sequence of responses leading to a terminal outcome. Each response and its associated stimulus condition produces a change that serves dual function: CONDITIONED REINFORCEMENT for the previous response and SD for the next. Reinforcement for the LAST response in the chain maintains the reinforcing effectiveness of all prior links.",
      ],
    },
    {
      title: "Three defining characteristics",
      body: [
        "A behavior chain (a) involves a specific series of discrete responses; (b) the performance of each behavior produces a stimulus change that reinforces the preceding response and serves as an SD for the next; and (c) the responses must be performed in a specific sequence in close temporal succession.",
      ],
    },
    {
      title: "Chains with limited holds",
      body: [
        "A chain with a limited hold requires the sequence to be performed correctly AND within a specified time for reinforcement. Proficient responding becomes part of the criterion. Set time criteria for individual responses, the total chain, or both.",
      ],
    },
    {
      title: "Why chains matter",
      body: [
        "Three reasons to master chaining: (a) chains improve independent-living skills; (b) chains let other behaviors combine into more complex sequences; (c) chains combine with other procedures to build behavioral repertoires in generalized settings.",
      ],
    },
    {
      title: "Task analysis",
      body: [
        "Task analysis breaks a complex skill into smaller, teachable units producing a sequentially ordered list of steps. Methods: observe a competent performer, execute the task yourself, ask an expert, or use trial and error.",
      ],
    },
    {
      title: "Mastery assessment: single- vs multiple-opportunity",
      body: [
        "Before teaching, assess which steps the learner can already perform. SINGLE-OPPORTUNITY: probe each step in order until the learner errs, then end the probe. MULTIPLE-OPPORTUNITY: probe every step regardless of earlier errors; produces a more complete baseline but is less time-efficient.",
      ],
    },
    {
      title: "Chaining methods",
      body: [
        "FORWARD chaining: teach Step 1 to criterion, then 1+2, then 1+2+3, etc. BACKWARD chaining: complete all steps except the last; reinforce on Step N; then reinforce on Steps N-1 + N; then N-2 + N-1 + N, etc. Learner contacts the terminal reinforcer EVERY trial. BACKWARD WITH LEAP AHEADS: skip training of some steps based on probes. TOTAL-TASK chaining: train ALL steps every session with prompts on steps the learner can't yet do.",
      ],
    },
    {
      title: "Choosing a chaining method",
      body: [
        "Base the decision on (a) the task analysis assessment, (b) empirically sound data-based studies, and (c) a functional evaluation of the learner's cognitive, physical, motoric, and attention abilities. Backward chaining is often preferred when contacting the terminal reinforcer matters. Total task suits learners with sufficient existing skills.",
      ],
    },
    {
      title: "Disrupting and breaking behavior chains",
      body: [
        "Chains can be interrupted or broken in at least six ways: EXTINCTION, SATIATION, UNCHAINING (removing the contingencies linking links), INTERRUPTING THE CHAIN (used in mand training), SUBSTITUTING AN SD, or EXTENDING the chain with time delays.",
      ],
    },
    {
      title: "Troubleshooting an inappropriate chain",
      body: [
        "When a chain misperforms, re-examine the S-R sequence; check if similar SDs cue different responses; analyze the job setting for relevant and irrelevant SDs; check whether job-setting SDs differ from training SDs; identify novel stimuli in the environment.",
      ],
    },
    {
      title: "Factors that affect chain performance",
      body: [
        "Completeness of the task analysis; length/complexity of the chain; schedule of reinforcement; stimulus variation; and response variation. All five must be evaluated when planning or maintaining a chain.",
      ],
    },
  ],
  glossary: [
    { term: "Behavior chain", definition: "Linked sequence of responses leading to a terminal outcome; each link's stimulus change reinforces the previous response and serves as SD for the next.", sourceHint: "A behavior chain is a linked sequence of responses leading to a terminal outcome." },
    { term: "Task analysis", definition: "Breaking a complex skill into smaller, teachable units; produces a sequentially ordered list of steps.", sourceHint: "Task analysis involves breaking a complex skill into smaller, teachable units, the product of which is a series of sequentially ordered steps or tasks." },
    { term: "Forward chaining", definition: "Teach behaviors in natural order; reinforcement follows criterion completion of all steps trained so far.", sourceHint: "In forward chaining, the behaviors identified in the task analysis are taught in their natural sequence." },
    { term: "Backward chaining", definition: "Trainer completes all but the last step; reinforce final step; then last two; etc. Learner contacts terminal reinforcer every trial.", sourceHint: "In backward chaining, all the steps identified in the task analysis are completed by the trainer, except the last one. When the final step in the sequence is performed to criterion, reinforcement is delivered." },
    { term: "Backward chaining with leap aheads", definition: "Backward chaining variant that probes some steps rather than training every one; speeds acquisition.", sourceHint: "Backward chaining with leap aheads follows essentially the same procedures as in backward chaining, except that not every step in the task analysis is trained." },
    { term: "Total-task chaining", definition: "Train ALL steps each session; use response prompts on steps the learner can't perform yet.", sourceHint: "Total-task chaining is a variation of forward chaining in which the learner receives training on each step in the task analysis during every session." },
    { term: "Single-opportunity method", definition: "Mastery assessment: probe each step in order until the learner errs.", sourceHint: "Assessment can be conducted with the single-opportunity or multiple-opportunity method." },
    { term: "Multiple-opportunity method", definition: "Mastery assessment: probe every step regardless of earlier errors.", sourceHint: "Assessment can be conducted with the single-opportunity or multiple-opportunity method." },
    { term: "Chain with limited hold", definition: "Behavior chain in which the sequence must be performed correctly AND within a specified time.", sourceHint: "In a behavior chain with a limited hold, a sequence of behaviors must be performed correctly and within a specific time for reinforcement to be delivered." },
  ],
  procedures: [
    {
      title: "Build a chain via task analysis",
      intro: "Start with a clean task analysis before any teaching.",
      steps: [
        { title: "Identify the terminal behavior", detail: "Specify the end product the chain produces." },
        { title: "Construct the task analysis", detail: "Observe an expert, perform the task, consult, or iterate via trial and error." },
        { title: "Validate task analysis", detail: "Verify the steps with another competent performer; revise as needed." },
        { title: "Assess mastery", detail: "Use single-opportunity or multiple-opportunity to identify which steps the learner already performs." },
        { title: "Pick a chaining method", detail: "Forward, backward, backward with leap aheads, or total task." },
      ],
    },
    {
      title: "Conduct backward chaining",
      intro: "Trainer completes all but the last step; reinforce that step first.",
      steps: [
        { title: "Train Step N", detail: "Trainer completes steps 1 through N-1; learner does Step N; reinforce on criterion." },
        { title: "Train Step N-1 and N", detail: "Learner now performs the last two steps to criterion before reinforcement." },
        { title: "Continue back through Step 1", detail: "Each session, the learner performs one additional preceding step." },
        { title: "Maintain", detail: "Once all steps performed independently, fade prompts and run on a maintenance schedule." },
      ],
    },
    {
      title: "Troubleshoot an inappropriate chain",
      intro: "Run the five-question diagnostic.",
      steps: [
        { title: "Re-examine SDs and responses", detail: "Is the S1-R1-S2-R2 sequence intact?" },
        { title: "Check for ambiguous SDs", detail: "Do similar SDs cue different responses in this learner's history?" },
        { title: "Analyze the setting", detail: "Identify relevant and irrelevant SDs in the job setting." },
        { title: "Compare training vs job-setting SDs", detail: "Have features changed?" },
        { title: "Look for novel stimuli", detail: "Has anything new entered the environment to disrupt the chain?" },
      ],
    },
  ],
  examples: [
    { scenario: "Handwashing taught from turning on the water -> wetting hands -> soaping -> rinsing -> drying.",
      application: "Forward chaining: reinforce Step 1, then Steps 1+2, etc.",
      sourceHint: "In forward chaining, the behaviors identified in the task analysis are taught in their natural sequence." },
    { scenario: "Teaching shoe tying by having the trainer perform all steps except the final knot; learner pulls the loops, gets praise.",
      application: "Backward chaining; learner contacts the terminal reinforcer every trial.",
      sourceHint: "In backward chaining, all the steps identified in the task analysis are completed by the trainer, except the last one. When the final step in the sequence is performed to criterion, reinforcement is delivered. The primary advantage of backward chaining is that the learner contacts the chain's terminal reinforcer on every instructional trial." },
    { scenario: "A learner who can already complete most steps of brushing teeth practices the entire chain each session with prompts on the few weak steps.",
      application: "Total-task chaining; efficient for partially proficient learners.",
      sourceHint: "Total-task chaining is a variation of forward chaining in which the learner receives training on each step in the task analysis during every session." },
  ],
  comparisons: [
    {
      title: "Forward vs Backward chaining",
      left:  { label: "Forward", points: [
        "Teach Step 1, then 1+2, ...",
        "Learner contacts terminal reinforcer LAST.",
        "Natural for newly acquired chains.",
      ]},
      right: { label: "Backward", points: [
        "Teach last step first; build backward.",
        "Learner contacts terminal reinforcer EVERY trial.",
        "Strong for motivation; popular for complex chains.",
      ]},
    },
    {
      title: "Single- vs Multiple-opportunity assessment",
      left:  { label: "Single-opportunity", points: [
        "Stop probing at first error.",
        "Fast; underestimates skills past the error.",
      ]},
      right: { label: "Multiple-opportunity", points: [
        "Probe every step regardless of errors.",
        "More complete baseline; more time-intensive.",
      ]},
    },
    {
      title: "Total-task vs Forward/Backward",
      left:  { label: "Total-task", points: [
        "All steps every session with prompts on weak ones.",
        "Efficient when learner has many existing skills.",
      ]},
      right: { label: "Forward / Backward", points: [
        "One step (or pair) per session.",
        "Better for learners with few existing skills.",
      ]},
    },
  ],
  misconceptions: [
    {
      myth: "A behavior chain is just a sequence; the order doesn't matter.",
      reality: "Order is essential. Each link's stimulus change reinforces the previous response AND serves as SD for the next, in a specific sequence.",
      sourceHint: "A behavior chain is a linked sequence of responses leading to a terminal outcome. Each response, and its associated stimulus condition, produces a change that functions as conditioned reinforcement for that previous response and as a discriminative stimulus for the next response in the chain.",
    },
    {
      myth: "Backward chaining is always better than forward.",
      reality: "Backward maximizes terminal-reinforcer contact, but choice depends on task analysis, learner abilities, and empirical evidence.",
      sourceHint: "The decision to use forward chaining, total-task chaining, backward chaining, or backward chaining with leap aheads should be based on the results of a task analysis assessment; empirically sound data-based studies; and a functional evaluation.",
    },
    {
      myth: "Chains break only through extinction.",
      reality: "Chains can be disrupted via extinction, satiation, unchaining, interrupting the chain, substituting an SD, or extending the chain with time delays.",
      sourceHint: "Behavior chains can be interrupted or broken in at least six ways. These include extinction, satiation, unchaining, interrupting the chain, substituting an SD, or extending the chain and building in time delays.",
    },
    {
      myth: "If a chain works in training it will work in the job setting.",
      reality: "Setting-specific SDs differ. Troubleshoot by examining the SDs and responses, ambiguous SDs, irrelevant setting features, and novel stimuli.",
      sourceHint: "To troubleshoot an inappropriate chain, the behavior analyst should consider (a) re-examining the SDs and responses... (c) analyzing the job setting to identify relevant and irrelevant SDs.",
    },
  ],
  examChecklist: [
    "Define a behavior chain and explain the dual function of each link.",
    "List the three defining characteristics of a behavior chain.",
    "Define task analysis and four methods for constructing one.",
    "Distinguish single-opportunity from multiple-opportunity mastery assessment.",
    "Describe forward, backward, backward with leap aheads, and total-task chaining.",
    "Compare forward and backward chaining on terminal-reinforcer contact.",
    "List six ways behavior chains can be disrupted or broken.",
    "List the five questions used to troubleshoot an inappropriate chain.",
    "List five factors that affect chain performance.",
  ],
  flashcardTerms: [
    "Behavior chain", "Task analysis",
    "Forward chaining", "Backward chaining", "Backward chaining with leap aheads", "Total-task chaining",
    "Single-opportunity method", "Multiple-opportunity method",
    "Chain with limited hold",
  ],
  quiz: [
    {
      id: "Q1",
      question: "In a behavior chain, each link's stimulus change serves which TWO functions?",
      options: [
        { label: "A", text: "EO for the next response AND punisher for the previous" },
        { label: "B", text: "Conditioned reinforcer for the previous response AND SD for the next" },
        { label: "C", text: "MO for the chain AND unconditioned reinforcer" },
        { label: "D", text: "Stimulus prompt AND fading cue" },
      ],
      answer: "B",
      explanation: "Each link is a conditioned reinforcer for the prior response and an SD for the next.",
      topic: "Chain structure",
      sourceHint: "Each stimulus that links two sequential responses in a chain serves dual functions: It is a conditioned reinforcer for the response that produced it and an SD for the next response in the chain.",
    },
    {
      id: "Q2",
      question: "Which chaining method has the learner contact the terminal reinforcer on EVERY instructional trial?",
      options: [
        { label: "A", text: "Forward chaining" },
        { label: "B", text: "Total-task chaining" },
        { label: "C", text: "Backward chaining" },
        { label: "D", text: "Backward chaining with leap aheads" },
      ],
      answer: "C",
      explanation: "Backward chaining trains the last step first; the learner contacts the terminal reinforcer at the end of every trial.",
      topic: "Chaining variants",
      sourceHint: "The primary advantage of backward chaining is that the learner contacts the chain's terminal reinforcer on every instructional trial.",
    },
    {
      id: "Q3",
      question: "In a chain with a limited hold:",
      options: [
        { label: "A", text: "Reinforcement requires only correct order, not speed" },
        { label: "B", text: "The sequence must be performed correctly AND within a specified time" },
        { label: "C", text: "Reinforcement is delivered noncontingently" },
        { label: "D", text: "Each step is reinforced individually" },
      ],
      answer: "B",
      explanation: "Limited holds add a time criterion; proficient responding becomes part of the reinforcement contingency.",
      topic: "Limited hold",
      sourceHint: "In a behavior chain with a limited hold, a sequence of behaviors must be performed correctly and within a specific time for reinforcement to be delivered.",
    },
    {
      id: "Q4",
      question: "Single-opportunity mastery assessment:",
      options: [
        { label: "A", text: "Probes every step regardless of errors" },
        { label: "B", text: "Probes each step in order and stops at the first error" },
        { label: "C", text: "Probes only the last step" },
        { label: "D", text: "Provides full prompts" },
      ],
      answer: "B",
      explanation: "Single-opportunity stops at the first error; multiple-opportunity continues through all steps.",
      topic: "Assessment",
      sourceHint: "Assessment can be conducted with the single-opportunity or multiple-opportunity method.",
    },
    {
      id: "Q5",
      question: "Which is NOT one of the six ways to disrupt a behavior chain?",
      options: [
        { label: "A", text: "Extinction" },
        { label: "B", text: "Satiation" },
        { label: "C", text: "Unchaining" },
        { label: "D", text: "Adding a stimulus fading procedure" },
      ],
      answer: "D",
      explanation: "The six listed methods are extinction, satiation, unchaining, interrupting the chain, substituting an SD, and extending/delaying the chain.",
      topic: "Disrupting chains",
      sourceHint: "Behavior chains can be interrupted or broken in at least six ways. These include extinction, satiation, unchaining, interrupting the chain, substituting an SD, or extending the chain and building in time delays.",
    },
  ],
  reviewPrompts: [
    "Construct a task analysis for a daily routine and validate it with one expert performer.",
    "Pick forward, backward, or total task for the routine and justify the choice.",
    "Sketch a backward chaining plan for tying shoes.",
    "Describe troubleshooting steps for a chain that fails in the natural environment but works in training.",
    "List five factors that affect chain performance and how you would assess each.",
  ],
  recap: [
    "A behavior chain is a sequence of responses in which each link reinforces the previous response and serves as SD for the next. Reinforcement for the final response maintains the entire chain.",
    "Task analysis breaks the chain into steps. Single-opportunity vs multiple-opportunity assessments identify mastery levels. Forward, backward, backward with leap aheads, and total-task chaining each fit different learners and tasks.",
    "Chains break via extinction, satiation, unchaining, interruption, SD substitution, or temporal extension. Troubleshoot using the SD-and-response sequence, ambiguous SDs, setting analysis, and novel stimuli. Five factors affect performance: completeness, length, schedule, stimulus variation, and response variation.",
  ],
  sourceExcerpts: [
    {
      heading: "Definition",
      text: "A behavior chain is a linked sequence of responses leading to a terminal outcome. Each response, and its associated stimulus condition, produces a change that functions as conditioned reinforcement for that previous response and as a discriminative stimulus for the next response in the chain. Reinforcement for the last response in a chain maintains the reinforcing effectiveness of the stimulus changes produced by all previous responses in the chain.",
    },
    {
      heading: "Chaining methods",
      text: "In forward chaining, the behaviors identified in the task analysis are taught in their natural sequence. In backward chaining, all the steps identified in the task analysis are completed by the trainer, except the last one. Total-task chaining is a variation of forward chaining in which the learner receives training on each step in the task analysis during every session.",
    },
    {
      heading: "Disrupting and troubleshooting",
      text: "Behavior chains can be interrupted or broken in at least six ways. These include extinction, satiation, unchaining, interrupting the chain, substituting an SD, or extending the chain and building in time delays. To troubleshoot an inappropriate chain, the behavior analyst should consider (a) re-examining the SDs and responses, (b) determining whether similar SDs cue different responses, (c) analyzing the job setting to identify relevant and irrelevant SDs, (d) determining whether SDs in the job setting differ from training SDs, and (e) identifying the presence of novel stimuli in the environment.",
    },
  ],
};

export default chapter23Module;
